import { ChangeEvent, FC, useContext } from 'react'
import { RadioButton } from 'components/atoms/inputs/RadioButton/RadioButton'
import RadioTextCombo from 'components/atoms/inputs/RadioTextCombo/RadioTextCombo'
import { currentCCLedgerSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { maskCurrencyFromZeroWithCents } from 'components/atoms/inputs/maskedInput'
import { PaneContext } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'
import { CreditCardFloatingPane } from 'components/features/creditCard/creditCardStore/floatingPane'
import { useFormContext } from 'react-hook-form'


export const SelectPaymentAmountFields: FC<{highlighted?: string}> = ({ highlighted }) => {
  const pane = useContext(PaneContext) as CreditCardFloatingPane
  const isAutoPay = pane?.mode === 'autoPay'
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const { setValue, watch } = useFormContext()
  const currentFixedAmount = watch('fixed_amount')

  if (!ledger) {
    return null
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value

    if (selectedOption === 'current') {
      setValue('amount', ledger.current_balance)
    } else if (selectedOption === 'statement') {
      setValue('amount', ledger.statement_balance_remaining)
    } else if (selectedOption === 'minimum') {
      setValue('amount', ledger.minimum_payment_remaining)
    } else if (selectedOption === 'fixed') {
      setValue('amount', currentFixedAmount)
    }

    setValue('payment_amount_type', selectedOption)
  }

  const commonProps = {
    name: 'payment_amount_type',
    withBorder: true,
    onChange: handleChange,
  }

  return (
    <fieldset>
      <RadioButton
        { ...commonProps }
        value='current'
        label='Current Balance'
        text='Your full balance, including payments made and credits received for this month and any previous carrying balance.'
        displayValue={ !isAutoPay ? formatUSCurrency(ledger.current_balance, 'penny') : undefined }
        isHighlighted={ highlighted === 'current' }
        disabled={ !isAutoPay && +ledger.current_balance <= 0 }
      />
      <RadioButton
        { ...commonProps }
        value='statement'
        label='Statement Balance'
        text='Your balance as of the last statement date, including payments made and credits received for this month.'
        displayValue={ !isAutoPay ? formatUSCurrency(ledger.statement_balance_remaining, 'penny') : undefined }
        isHighlighted={ highlighted === 'statement' }
        disabled={ !isAutoPay && +ledger.statement_balance_remaining <= 0 }
      />
      <RadioButton
        { ...commonProps }
        value='minimum'
        label='Minimum Amount Due'
        text='The amount you need to pay each month to keep your account in good standing.'
        displayValue={ !isAutoPay ? formatUSCurrency(ledger.minimum_payment_remaining, 'penny') : undefined }
        isHighlighted={ highlighted === 'minimum' }
      />
      <input type='hidden' name='amount' />
      {
        !isAutoPay && (
          <RadioTextCombo
            name='fixed_amount'
            label='Other Amount'
            radioName='payment_amount_type'
            radioValue='fixed'
            isHighlighted={ highlighted === 'fixed' }
            mask={ maskCurrencyFromZeroWithCents }
            inputType='currency'
            withBorder
            onRadioChange={ handleChange }
            onChange={ (event: ChangeEvent<HTMLInputElement>) => {
              setValue('amount', event.target.value)
              setValue('fixed_amount', event.target.value)
            } }
          />
        )
      }
    </fieldset>
  )
}
